import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Typography } from "@material-ui/core";
import { CloseButton, DialogHeader, ExampleLink } from "./styled";
import CloseIcon from "@material-ui/icons/Close";
import ExampleView from "./ExampleView";
import ImportView from "./ImportView";
import AccountsView from "./AccountsView";
import SavedResultsView from "./SavedResultsView";
import FormatErrorsView from "./FormatErrorsView";
import reducer, { init } from "./reducer";
import SavingAccountsView from "./SavingAccountsView";
import SelectColumnsView from "./SelectColumnsView";
import SelectedColumns from "./SelectedColumns";

export type ActionType = "UPSERT" | "UPDATE" | "DELETE";
interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: ActionType | null;
}

export default function ImportAccountsModal({ isOpen, onClose, type }: Props) {
  const { t } = useTranslation("accounts");
  const [state, dispatch] = useReducer(reducer, init(type));

  const { currentView } = state;

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <DialogHeader disableTypography>
        <Typography variant="h6">{t(`modal.title.${type}`)}</Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginBottom: "10px", maxWidth: "80%" }}
        >
          {t(`modal.subtitle.${type}`)}
        </Typography>
        {currentView !== "EXAMPLE" && type !== "DELETE" && (
          <ExampleLink
            variant="caption"
            onClick={() => dispatch({ type: "SHOW_EXAMPLE" })}
          >
            {t("importAccountsModal.example.link")}
          </ExampleLink>
        )}
        <CloseButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogHeader>

      {currentView === "SELECT_COLUMNS" && (
        <SelectColumnsView
          onChange={selection =>
            dispatch({
              type: "SELECTED_COLUMNS",
              attributes: selection,
            })
          }
          selectedColumns={state.selectedColumns}
        />
      )}

      {currentView === "EXAMPLE" && (
        <ExampleView back={() => dispatch({ type: "BACK_FROM_EXAMPLE" })} />
      )}
      {currentView === "IMPORT" && (
        <>
          {state.selectedColumns.length > 0 && (
            <SelectedColumns
              selected={state.selectedColumns}
              onChange={() => dispatch({ type: "SHOW_SELECT_COLUMNS" })}
            />
          )}
          <ImportView dispatch={dispatch} state={state} />
        </>
      )}
      {currentView === "ACCOUNTS" && (
        <AccountsView
          selectedFile={state.selectedFile}
          back={() => dispatch({ type: "SHOW_IMPORT" })}
          columns={
            state.action === "UPDATE" ? state.selectedColumns : undefined
          }
        />
      )}

      {currentView === "SAVING_ACCOUNTS" && (
        <SavingAccountsView
          state={state}
          dispatch={dispatch}
          closeModal={onClose}
        />
      )}

      {currentView === "SAVED_RESULTS" && (
        <SavedResultsView
          state={state}
          back={() => dispatch({ type: "SHOW_SAVING_ACCOUNTS" })}
        />
      )}

      {currentView === "FORMAT_ERRORS" && (
        <FormatErrorsView
          selectedFile={state.selectedFile}
          back={() => dispatch({ type: "SHOW_IMPORT" })}
        />
      )}
    </Dialog>
  );
}
